import { Building2, TrendingUp, Target, Users, BarChart3, Shield, LineChart, Megaphone, Settings, Database } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "Performance Transformation",
    icon: TrendingUp,
    components: [
      "Digital marketing audit and benchmarking",
      "Operating model assessment",
      "Capability gap analysis",
    ],
    outcome: "Clear roadmap for marketing performance improvement.",
  },
  {
    name: "Media Consolidation",
    icon: Target,
    components: [
      "Agency and vendor rationalization",
      "Budget reallocation optimization",
      "Cross-brand media synergies",
    ],
    outcome: "More efficient spend with better results.",
  },
  {
    name: "Data & Measurement",
    icon: BarChart3,
    components: [
      "Marketing mix modeling",
      "Multi-touch attribution",
      "Executive dashboard development",
    ],
    outcome: "Credible measurement that drives decisions.",
  },
  {
    name: "Team Enablement",
    icon: Users,
    components: [
      "In-house capability development",
      "Training and knowledge transfer",
      "Change management support",
    ],
    outcome: "Build lasting internal capabilities.",
  },
  {
    name: "Governance & Process",
    icon: Shield,
    components: [
      "Marketing governance frameworks",
      "Performance review cadences",
      "Quality assurance standards",
    ],
    outcome: "Sustainable processes that maintain performance.",
  },
  {
    name: "Technology Integration",
    icon: Building2,
    components: [
      "MarTech stack assessment",
      "Platform selection and implementation",
      "Integration architecture",
    ],
    outcome: "Technology that enables rather than constrains.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Executive Alignment",
    description: "Align with leadership on objectives, constraints, and success criteria.",
  },
  {
    number: "02",
    title: "Comprehensive Assessment",
    description: "Deep dive into current performance, capabilities, and opportunities.",
  },
  {
    number: "03",
    title: "Strategy & Roadmap",
    description: "Develop phased transformation plan with clear milestones and ownership.",
  },
  {
    number: "04",
    title: "Pilot & Prove",
    description: "Execute initial initiatives to demonstrate value and refine approach.",
  },
  {
    number: "05",
    title: "Scale & Transition",
    description: "Expand successful initiatives and transfer capabilities to internal teams.",
  },
];

// Enhanced timeline phases for enterprise transformation
const timelinePhases = [
  {
    number: "01",
    title: "Executive Alignment",
    description: "Align leadership on objectives and success criteria.",
    decision: "What transformation success looks like.",
    output: "Signed-off success criteria and governance model.",
    riskRemoved: "Misaligned stakeholder expectations.",
  },
  {
    number: "02",
    title: "Comprehensive Assessment",
    description: "Deep dive into performance, capabilities, and opportunities.",
    decision: "Where the biggest value gaps exist.",
    output: "Diagnostic report with prioritized opportunities.",
    riskRemoved: "Surface-level analysis missing root causes.",
  },
  {
    number: "03",
    title: "Strategy & Roadmap",
    description: "Develop phased plan with clear milestones.",
    decision: "How to sequence for maximum value realization.",
    output: "Transformation roadmap with ownership matrix.",
    riskRemoved: "Over-ambitious plans that stall.",
  },
  {
    number: "04",
    title: "Pilot & Prove",
    description: "Execute initial initiatives to demonstrate value.",
    decision: "What to pilot to build organizational momentum.",
    output: "Proven playbooks with measured results.",
    riskRemoved: "Change resistance from unproven concepts.",
  },
  {
    number: "05",
    title: "Scale & Transition",
    description: "Expand success and transfer capabilities internally.",
    decision: "How to institutionalize new capabilities.",
    output: "Self-sustaining performance organization.",
    riskRemoved: "Dependency on external support.",
  },
];

const technologyCategories = [
  { title: "Analytics", icon: LineChart, items: ["Google Analytics 360", "Adobe Analytics", "Snowflake", "Tableau"] },
  { title: "Media", icon: Megaphone, items: ["Google SA360", "DV360", "Meta Business Suite", "The Trade Desk"] },
  { title: "MarTech", icon: Settings, items: ["Salesforce", "Adobe Experience Cloud", "HubSpot", "Oracle"] },
  { title: "Data", icon: Database, items: ["BigQuery", "Looker", "dbt", "Fivetran"] },
];

const faqs = [
  {
    question: "How do you work with existing agencies and vendors?",
    answer: "We can work alongside existing partners, manage them, or help you transition to a new model. Our goal is optimal outcomes, not vendor displacement for its own sake.",
  },
  {
    question: "What size enterprises do you work with?",
    answer: "We typically work with companies spending $10M+ annually on digital marketing, though we also work with high-growth companies approaching that scale.",
  },
  {
    question: "How do you handle internal politics and resistance to change?",
    answer: "Change management is built into our approach. We work with stakeholders at all levels and focus on quick wins to build momentum and credibility.",
  },
  {
    question: "What's your approach to data privacy and compliance?",
    answer: "We work within your compliance framework and help ensure marketing activities meet regulatory requirements including GDPR, CCPA, and industry-specific regulations.",
  },
  {
    question: "How long do enterprise engagements typically last?",
    answer: "Initial assessments take 6-8 weeks. Transformation programs typically run 12-24 months, with clear phase gates and value realization along the way.",
  },
];

const qualification = {
  forYou: [
    "You're spending $10M+ annually on digital marketing",
    "You suspect you're not getting the ROI you should from marketing",
    "You're willing to make organizational changes to improve performance",
    "You have executive sponsorship for a transformation initiative",
  ],
  notForYou: [
    "You're looking for tactical campaign execution only",
    "You're not prepared for the organizational change required",
    "You need results in weeks rather than months",
    "You don't have executive buy-in for a strategic partnership",
  ],
};

const EnterprisePerformance = () => {
  return (
    <SubServicePageLayout
      seoTitle="Enterprise Performance Marketing Services"
      seoDescription="Digital marketing transformation for large organizations. Performance audits, media consolidation, and capability building for enterprise-scale results."
      canonicalUrl="https://xperfomax.com/solutions/enterprise-performance"
      category="solutions"
      categoryLabel="Solutions / Enterprise Performance"
      headline="Enterprise performance, reimagined."
      subheadline="We help large organizations transform their digital marketing performance through strategic assessment, process improvement, and capability building."
      problemTitle="Why enterprise marketing underperforms."
      problems={[
        "Fragmented agencies and vendors operate without unified strategy.",
        "Measurement frameworks fail to connect activity to business outcomes.",
        "Internal capabilities have not kept pace with market changes.",
        "Governance gaps prevent optimization and accountability at scale.",
      ]}
      systemTitle="A transformation system for enterprise."
      systemDescription="Strategic assessment, operational improvement, and capability building."
      services={services}
      processTitle="From assessment to transformation."
      processSteps={processSteps}
      timelinePhases={timelinePhases}
      technologyCategories={technologyCategories}
      techTitle="Platforms"
      techDescription="We work with enterprise-grade tools and help optimize your existing stack."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready to transform?"
      finalCtaDescription="Let's discuss how we can help you unlock the performance your marketing investment deserves."
      finalCtaButtonText="Start a serious conversation"
    />
  );
};

export default EnterprisePerformance;
